import { Shield, CheckCircle2, XCircle, AlertTriangle, Key } from 'lucide-react';
import { useSealConfigurations, useSealWarnings } from '../hooks/useApi';
import { StatusBadge } from '../components/shared/StatusBadge';
import { AlertCard } from '../components/shared/AlertCard';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ErrorAlert } from '../components/shared/ErrorAlert';
import type { SealConfigurationInfo } from '../types/api';

function SignatureUsageBar({ used, total }: { used: number; total: number }) {
  const pct = total > 0 ? (used / total) * 100 : 0;
  const remaining = total - used;
  const color = pct > 90 ? 'bg-red-500' : pct > 75 ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div>
      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
        <span>Zuzyto: {used.toLocaleString('pl-PL')}</span>
        <span>Pozostalo: {remaining.toLocaleString('pl-PL')}</span>
      </div>
      <div className="h-2 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-gray-500 mt-1">Limit: {total.toLocaleString('pl-PL')} ({pct.toFixed(1)}% zuzyto)</p>
    </div>
  );
}

function SealCard({ config }: { config: SealConfigurationInfo }) {
  const certValidTo = config.certificateValidTo ? new Date(config.certificateValidTo) : null;
  const now = new Date();
  const isExpired = certValidTo && certValidTo < now;
  const isExpiringSoon = certValidTo && !isExpired && certValidTo < new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  return (
    <div className="card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">{config.name}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{config.organisationName}</p>
        </div>
        {config.isActive ? (
          <StatusBadge label="Aktywna" variant="success" />
        ) : (
          <StatusBadge label="Nieaktywna" variant="neutral" />
        )}
      </div>

      {/* Certificate Info */}
      <div className="space-y-3 mb-4">
        {config.certificateSubjectName && (
          <div className="flex items-start gap-2">
            <Key className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Podmiot certyfikatu</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">{config.certificateSubjectName}</p>
            </div>
          </div>
        )}

        {config.certificateIssuerName && (
          <div className="text-xs text-gray-500">
            Wystawca: {config.certificateIssuerName}
          </div>
        )}

        {certValidTo && (
          <div className="flex items-center gap-2">
            {isExpired ? (
              <XCircle className="h-4 w-4 text-red-400" />
            ) : isExpiringSoon ? (
              <AlertTriangle className="h-4 w-4 text-amber-400" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            )}
            <span className={`text-sm ${isExpired ? 'text-red-600 dark:text-red-300' : isExpiringSoon ? 'text-amber-600 dark:text-amber-300' : 'text-gray-700 dark:text-gray-300'}`}>
              Wazny do: {certValidTo.toLocaleDateString('pl-PL')}
            </span>
          </div>
        )}
      </div>

      {/* Signatures */}
      {config.totalSignatures !== null && config.usedSignatures !== null && (
        <SignatureUsageBar used={config.usedSignatures} total={config.totalSignatures} />
      )}

      {config.remainingSignatures === null && config.isActive && (
        <p className="text-xs text-gray-500 italic">Nie udalo sie pobrac statusu z EuroCert API</p>
      )}

      {/* Tasks */}
      {config.tasks.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-800">
          <p className="text-xs text-gray-500 mb-2">Zadania podpisu:</p>
          <div className="flex flex-wrap gap-2">
            {config.tasks.map((task, i) => (
              <StatusBadge
                key={i}
                label={task.signatureTypeName}
                variant={task.isActive ? 'info' : 'neutral'}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function SealsPage() {
  const configs = useSealConfigurations();
  const warnings = useSealWarnings();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <Shield className="h-6 w-6 text-brand-600 dark:text-brand-400" />
          Pieczeci kwalifikowane
        </h1>
        <p className="text-sm text-gray-500 mt-1">Konfiguracja i status pieczeci EuroCert</p>
      </div>

      {/* Warnings */}
      {warnings.data && warnings.data.length > 0 && (
        <div className="space-y-2">
          {warnings.data.map((w, i) => (
            <AlertCard
              key={i}
              title={`${w.sealName}${w.organisationName ? ` (${w.organisationName})` : ''}`}
              message={w.message}
              severity={w.severity}
            />
          ))}
        </div>
      )}

      {/* Seal Configs */}
      {configs.isLoading ? (
        <LoadingSpinner label="Ladowanie konfiguracji pieczeci..." />
      ) : configs.error ? (
        <ErrorAlert message={configs.error.message} onRetry={() => configs.refetch()} />
      ) : configs.data ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {configs.data.map((config) => (
            <SealCard key={config.id} config={config} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
